/**
 * Vertical shift (px) for mobile battle HUD: lifts player bottom dock (general, health, O/S/C, hand)
 * away from resign / end-turn, and shifts opponent top dock (avatars, intel, O/S/C, drones) by the
 * same amount from the top edge. Keep in sync across mobile battle components.
 */
export const MOBILE_BATTLE_DOCK_LIFT_PX = 12;

/** Opponent drone row: 60px slot × 0.35 scale (see MobileOpponentDrones). */
export const MOBILE_OPPONENT_DRONE_SLOT_HEIGHT_PX = 60;
export const MOBILE_OPPONENT_DRONE_SCALE = 0.35;
export const MOBILE_OPPONENT_DRONE_ROW_TOP_PX = 5 + MOBILE_BATTLE_DOCK_LIFT_PX;
export const MOBILE_OPPONENT_DRONE_ROW_VISUAL_HEIGHT_PX = Math.round(
  MOBILE_OPPONENT_DRONE_SLOT_HEIGHT_PX * MOBILE_OPPONENT_DRONE_SCALE
);
/** O / S / C counters sit just under the drone strip (upper-right). */
export const MOBILE_OPPONENT_STATS_TOP_PX =
  MOBILE_OPPONENT_DRONE_ROW_TOP_PX + MOBILE_OPPONENT_DRONE_ROW_VISUAL_HEIGHT_PX + 6;

/** Opponent HUD cluster: top-center on mobile landscape (see BattleOpponentHudPanel.css). */
export const MOBILE_OPPONENT_AVATARS_TOP_PX = 6;
export const MOBILE_OPPONENT_HUD_SCALE = 0.405;

export const MOBILE_END_TURN_BOTTOM_PX = 5;

/** Player HUD: bottom-left, anchored near end-turn row (resign removed on mobile). */
export const MOBILE_PLAYER_HUD_LEFT_PX = 5;
export const MOBILE_HAND_CAROUSEL_BOTTOM_PX = 45 + MOBILE_BATTLE_DOCK_LIFT_PX;
export const MOBILE_PLAYER_HUD_BOTTOM_PX = MOBILE_END_TURN_BOTTOM_PX + 2;
/** Prior on-screen scale was 0.416; bump for a visible enlargement. */
export const MOBILE_PLAYER_HUD_BASE_SCALE = 0.416;
export const MOBILE_PLAYER_HUD_SCALE_BOOST = 1.3;
/** Single knob for player-HUD size on mobile — set on .player-hud-container--mobile via inline --hud-scale. */
export const MOBILE_PLAYER_HUD_SCALE = 0.70;
export const MOBILE_PLAYER_HUD_NATIVE_WIDTH_PX = 410;
export const MOBILE_PLAYER_HUD_VISUAL_WIDTH_PX = Math.round(
  MOBILE_PLAYER_HUD_NATIVE_WIDTH_PX * MOBILE_PLAYER_HUD_SCALE
);
/** Prep/accord column — native coords inside scaled HUD, then canvas px. */
export const MOBILE_PLAYER_HUD_PROFILE_BUTTON_NATIVE_PX = 47;
export const MOBILE_PLAYER_HUD_PROFILE_STRIP_OFFSET_NATIVE_PX = 6;
export const MOBILE_PLAYER_HUD_PROFILE_STRIP_LEFT_PX =
  MOBILE_PLAYER_HUD_LEFT_PX +
  Math.round(
    (MOBILE_PLAYER_HUD_NATIVE_WIDTH_PX + MOBILE_PLAYER_HUD_PROFILE_STRIP_OFFSET_NATIVE_PX) *
      MOBILE_PLAYER_HUD_SCALE
  );
export const MOBILE_PLAYER_HUD_PROFILE_STRIP_RIGHT_PX =
  MOBILE_PLAYER_HUD_PROFILE_STRIP_LEFT_PX +
  Math.round(MOBILE_PLAYER_HUD_PROFILE_BUTTON_NATIVE_PX * MOBILE_PLAYER_HUD_SCALE);

/** Mobile battle modals (strategy + completed orders) — readable, 932×430 canvas coords (not vw/vh). */
export const MOBILE_VIEWPORT_BASE_HEIGHT = 430;
export const MOBILE_BATTLE_MODAL_PANEL_WIDTH_PX = 320;
export const MOBILE_BATTLE_MODAL_PANEL_MAX_HEIGHT_PX = 320;
export const MOBILE_BATTLE_MODAL_LIST_MAX_HEIGHT_PX = 200;

export const MOBILE_EMOTE_PICKER_MIN_WIDTH_PX = 148;
export const MOBILE_EMOTE_PICKER_MAX_WIDTH_PX = 188;
export const MOBILE_EMOTE_PICKER_MAX_HEIGHT_PX = 176;
export const MOBILE_EMOTE_PICKER_TOP_PX = 168;

/** Player drone row — same canvas coords as desktop `.player-drone-zone` (BattleUI.js). */
export const MOBILE_VIEWPORT_BASE_WIDTH = 932;
export const DESKTOP_PLAYER_BOTTOM_STRIP_BOTTOM_PX = 20;
export const DESKTOP_PLAYER_DRONE_ZONE_BOTTOM_PX = 78;
export const DESKTOP_PLAYER_DRONE_ZONE_RIGHT_PX = 20;
/** Keep the desktop gap between bottom-strip and drone row when mobile end-turn uses bottom 5. */
export const MOBILE_PLAYER_DRONE_BOTTOM_PX =
  MOBILE_END_TURN_BOTTOM_PX +
  (DESKTOP_PLAYER_DRONE_ZONE_BOTTOM_PX - DESKTOP_PLAYER_BOTTOM_STRIP_BOTTOM_PX);
export const MOBILE_PLAYER_DRONE_SLOT_COUNT = 4;
export const MOBILE_PLAYER_DRONE_SLOT_WIDTH_PX = 80;
export const MOBILE_PLAYER_DRONE_SLOT_HEIGHT_PX = 60;
export const MOBILE_PLAYER_DRONE_SCALE = 0.35;
export const MOBILE_PLAYER_DRONE_RIGHT_PX = DESKTOP_PLAYER_DRONE_ZONE_RIGHT_PX;
export const MOBILE_PLAYER_DRONE_GAP_PX = 8;
export const MOBILE_PLAYER_DRONE_ROW_UNSCALED_WIDTH_PX =
  MOBILE_PLAYER_DRONE_SLOT_COUNT * MOBILE_PLAYER_DRONE_SLOT_WIDTH_PX +
  (MOBILE_PLAYER_DRONE_SLOT_COUNT - 1) * MOBILE_PLAYER_DRONE_GAP_PX;
export const MOBILE_PLAYER_DRONE_ROW_VISUAL_WIDTH_PX = Math.round(
  MOBILE_PLAYER_DRONE_ROW_UNSCALED_WIDTH_PX * MOBILE_PLAYER_DRONE_SCALE
);

/**
 * Mobile hand carousel lane: left edge 16px past prep/accord column, right edge before drones.
 * Cards spread across the full lane (no inner left pad).
 */
export const MOBILE_HAND_CAROUSEL_PROFILE_CLEARANCE_PX = 16;
export const MOBILE_HAND_CARD_BASE_WIDTH_PX = 200;
export const MOBILE_HAND_CARD_SCALE = 0.52;
export const MOBILE_HAND_SCALED_CARD_WIDTH_PX = Math.round(
  MOBILE_HAND_CARD_BASE_WIDTH_PX * MOBILE_HAND_CARD_SCALE
);
export const MOBILE_HAND_CAROUSEL_LEFT_PX =
  MOBILE_PLAYER_HUD_PROFILE_STRIP_RIGHT_PX + MOBILE_HAND_CAROUSEL_PROFILE_CLEARANCE_PX;
export const MOBILE_HAND_CAROUSEL_LANE_LEFT_PAD_PX = 0;
export const MOBILE_HAND_DRONE_CLEARANCE_PX = 10;
export const MOBILE_HAND_CAROUSEL_RIGHT_PX =
  MOBILE_VIEWPORT_BASE_WIDTH -
  MOBILE_PLAYER_DRONE_RIGHT_PX -
  MOBILE_PLAYER_DRONE_ROW_VISUAL_WIDTH_PX -
  MOBILE_HAND_DRONE_CLEARANCE_PX;
export const MOBILE_HAND_CAROUSEL_AVAILABLE_WIDTH_PX =
  MOBILE_HAND_CAROUSEL_RIGHT_PX - MOBILE_HAND_CAROUSEL_LEFT_PX;

